import { useMemo, useRef } from 'react';
import { useAuth } from '@src/auth/AuthContext';
import { useTerminalAccount } from '@src/context/AccountContext';
import { getOrCreateClient, processResponse } from './TimePunchClockApi';
import type { PunchResponse } from './punchTypes';

export function useTimePunchClockApi(): {
    punch: ((pin: string) => Promise<PunchResponse>) | null;
} {
    const { accessToken } = useAuth();
    const { account } = useTerminalAccount();

    // Keep the latest token in a ref so the KMP tokenProvider always reads
    // the current token without requiring re-creation of the singleton client.
    const tokenRef = useRef<string | null>(accessToken);
    tokenRef.current = accessToken;

    // Stable provider function — created once, always reads from the ref above.
    const tokenProvider = useRef(() => tokenRef.current ?? '').current;

    const punch = useMemo<((pin: string) => Promise<PunchResponse>) | null>(
        () =>
            account
                ? (pin: string) => {
                      const client = getOrCreateClient(
                          {
                              userId: account.userId,
                              companyId: account.companyId,
                              locationHash: account.locationHash,
                              locationId: account.locationId
                          },
                          tokenProvider
                      );
                      return client
                          .punchAsPromise(pin)
                          .then(r => processResponse(r));
                  }
                : null,
        // tokenProvider is stable (never changes), so only account matters here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [account]
    );

    return { punch };
}
